import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  StatCard,
  ContentCard,
  DashboardHeader,
} from "../common/DashboardComponents";
import Loader from "../common/Loader";
import { fetchAllUsers } from "../../redux/actions/grievanceActions1";
import "../../styles/AresaUserList.css";

// Modern Icons
const Icons = {
  Users: <i className="bi bi-people-fill"></i>,
  User: <i className="bi bi-person-fill"></i>,
  Filter: <i className="bi bi-funnel-fill"></i>,
  Search: <i className="bi bi-search"></i>,
  Calendar: <i className="bi bi-calendar-date"></i>,
  Mobile: <i className="bi bi-phone"></i>,
  ID: <i className="bi bi-fingerprint"></i>,
  ServiceNumber: <i className="bi bi-card-heading"></i>,
  Status: <i className="bi bi-check-circle"></i>,
  Actions: <i className="bi bi-three-dots-vertical"></i>,
  ViewDetails: <i className="bi bi-eye"></i>,
  FirstPage: <i className="bi bi-chevron-double-left"></i>,
  PrevPage: <i className="bi bi-chevron-left"></i>,
  NextPage: <i className="bi bi-chevron-right"></i>,
  LastPage: <i className="bi bi-chevron-double-right"></i>,
};

const AresaUserList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Get state from Redux
  const { users, loading, error } = useSelector((state) => state.grievances);
  console.log(users, "userssss");

  // Local state
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage, setUsersPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({
    key: "name",
    direction: "ascending",
  });

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const userData = JSON.parse(localStorage.getItem("user"));
        await dispatch(fetchAllUsers());
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };

    fetchData();
  }, [dispatch]);



  // Stats calculations
  const userStats = useMemo(() => {
    const totalUsers = users.length;
    const approvedUsers = users.filter(
      (user) => user.IS_STATUS === 1 || user.IS_STATUS === "1"
    ).length;
    const pendingUsers = totalUsers - approvedUsers;

    return {
      totalUsers,
      approvedUsers,
      pendingUsers,
    };
  }, [users]);

  // Sort users based on sort configuration
  const sortedUsers = useMemo(() => {
    const sortableUsers = [...users];
    if (sortConfig.key) {
      sortableUsers.sort((a, b) => {
        // Handle null or undefined values
        if (!a[sortConfig.key])
          return sortConfig.direction === "ascending" ? -1 : 1;
        if (!b[sortConfig.key])
          return sortConfig.direction === "ascending" ? 1 : -1;

        // Compare values based on sort direction
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableUsers;
  }, [users, sortConfig]);

  // Request sort handler
  const requestSort = useCallback((key) => {
    setSortConfig((prevConfig) => {
      // If clicking the same column, toggle direction
      if (prevConfig.key === key) {
        return {
          key,
          direction:
            prevConfig.direction === "ascending" ? "descending" : "ascending",
        };
      }
      // Otherwise, sort the new column in ascending order
      return { key, direction: "ascending" };
    });
  }, []);

  // Get sort icon based on current sort config
  const getSortIcon = useCallback(
    (key) => {
      if (sortConfig.key !== key) {
        return <i className="bi bi-arrow-down-up text-muted sort-icon"></i>;
      }
      return sortConfig.direction === "ascending" ? (
        <i className="bi bi-sort-down sort-icon active"></i>
      ) : (
        <i className="bi bi-sort-up sort-icon active"></i>
      );
    },
    [sortConfig]
  );

  // Filter users based on search and status
  useEffect(() => {
    let result = [...users];

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(
        (user) =>
          (user?.USER_NAME &&
            user?.USER_NAME?.toLowerCase()?.includes(searchLower)) ||
          (user?.EMAIL && user?.EMAIL?.toLowerCase()?.includes(searchLower)) ||
          (user?.SERVICE_NO &&
            user?.SERVICE_NO?.toLowerCase()?.includes(searchLower)) ||
          (user?.MO_NO && user?.MO_NO.toLowerCase()?.includes(searchLower)) ||
          (user?.PPO_NO && user?.PPO_NO?.toLowerCase()?.includes(searchLower))
      );
    }

    // Apply status filter
    if (statusFilter !== "") {
      result = result.filter((user) => user?.IS_STATUS == statusFilter);
    }

    setFilteredUsers(result);
    setCurrentPage(1); // Reset to first page when filters change
  }, [sortedUsers, searchTerm, statusFilter]);

  // Event handlers
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
  };

  const handleItemsPerPageChange = (e) => {
    setUsersPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to first page
  };

  const handleViewDetails = (user) => {
    navigate(`/AresaUserDetails`, {
      state: {
        id: user.id,
        userDetails: user,
      },
    });
  };

  // Pagination calculations
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  // Pagination handlers
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const nextPage = () =>
    currentPage < totalPages && setCurrentPage(currentPage + 1);
  const prevPage = () => currentPage > 1 && setCurrentPage(currentPage - 1);

  // Get visible page numbers
  const getVisiblePageNumbers = () => {
    const delta = 1; // Number of pages to show before and after current page
    const range = [];
    const rangeWithDots = [];
    let l;

    // Always show first page
    range.push(1);

    // Calculate range of pages to show
    for (let i = currentPage - delta; i <= currentPage + delta; i++) {
      if (i > 1 && i < totalPages) {
        range.push(i);
      }
    }

    // Always show last page
    if (totalPages > 1) {
      range.push(totalPages);
    }

    // Add dots where needed
    for (let i of range) {
      if (l) {
        if (i - l === 2) {
          rangeWithDots.push(l + 1);
        } else if (i - l !== 1) {
          rangeWithDots.push("...");
        }
      }
      rangeWithDots.push(i);
      l = i;
    }

    return rangeWithDots;
  };

  return (
    <div className="container-fluid">
      <DashboardHeader title="Registration List" />

      {/* Statistics Cards */}
      <div className="stats-grid">
        <StatCard
          title="Total Users"
          value={userStats.totalUsers}
          icon={Icons.Users}
          color="#4A6CF7"
          additionalInfo={
            <>
              <span className="me-2">Approved: {userStats.approvedUsers}</span>
              <span>Pending: {userStats.pendingUsers}</span>
            </>
          }
        />
      </div>

      {/* Filters and Search */}
      <ContentCard title="Search & Filters" icon={Icons.Filter}>
        <div className="modern-filters">
          <div className="search-box">
            <div className="search-icon">{Icons.Search}</div>
            <input
              type="text"
              className="search-input"
              placeholder="Search by name, service number, mobile..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
            {searchTerm && (
              <button
                className="search-clear"
                onClick={() => setSearchTerm("")}
                aria-label="Clear search"
              >
                <i className="bi bi-x-circle-fill"></i>
              </button>
            )}
          </div>

          <div className="filter-controls">
            <div className="status-filter">
              <label>Status:</label>
              <select
                className="modern-select"
                value={statusFilter}
                onChange={handleStatusFilterChange}
              >
                <option value="">All</option>
                <option value="1">Approved</option>
                <option value="0">Pending</option>
                <option value="2">Reject</option>
              </select>
            </div>

            <div className="per-page-filter">
              <label>Show:</label>
              <select
                className="modern-select"
                value={usersPerPage}
                onChange={handleItemsPerPageChange}
              >
                <option value={5}>05</option>
                <option value={10}>10</option>
                <option value={15}>15</option>
                <option value={20}>20</option>
              </select>
            </div>
          </div>
        </div>
      </ContentCard>

      {/* Users Table */}
      <ContentCard title="Users List" icon={Icons.User}>
        {loading ? (
          <Loader message="Loading users..." />
        ) : (
          <div className="users-container">
            <div className="modern-table-container">
              <table className="modern-table">
                <thead>
                  <tr>
                    <th
                      className="sortable-header"
                      onClick={() => requestSort("id")}
                    >
                      <div className="header-content">
                        <span>User ID</span>
                        {getSortIcon("id")}
                      </div>
                    </th>
                    <th
                      className="sortable-header"
                      onClick={() => requestSort("name")}
                    >
                      <div className="header-content">
                        <span>Name</span>
                        {getSortIcon("name")}
                      </div>
                    </th>
                    <th
                      className="sortable-header"
                      onClick={() => requestSort("serviceNumber")}
                    >
                      <div className="header-content">
                        <span>Service Number</span>
                        {getSortIcon("serviceNumber")}
                      </div>
                    </th>
                    <th
                      className="sortable-header"
                      onClick={() => requestSort("mobileNumber")}
                    >
                      <div className="header-content">
                        <span>Mobile</span>
                        {getSortIcon("mobileNumber")}
                      </div>
                    </th>
                    <th
                      className="sortable-header"
                      onClick={() => requestSort("joinDate")}
                    >
                      <div className="header-content">
                        <span>DOB</span>
                        {getSortIcon("joinDate")}
                      </div>
                    </th>
                    <th
                      className="sortable-header"
                      onClick={() => requestSort("status")}
                    >
                      <div className="header-content">
                        <span>Status</span>
                        {getSortIcon("status")}
                      </div>
                    </th>
                    <th>
                      <div className="header-content">
                        <span>Actions</span>
                      </div>
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {currentUsers.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="empty-table">
                        <div className="empty-state">
                          <div className="empty-icon">
                            <i className="bi bi-person-x"></i>
                          </div>
                          <h4>No Users Found</h4>
                          <p>
                            {users.length === 0
                              ? "No users found in database"
                              : "No users match your filters"}
                          </p>
                          {searchTerm && (
                            <button
                              className="modern-button outlined"
                              onClick={() => setSearchTerm("")}
                            >
                              Clear Search
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    currentUsers.map((user) => (
                      <tr key={user.id} className="data-row">
                        <td>
                          <div className="user-id">
                            <span className="id-badge">
                              U{user?.USER_ID.toString().padStart(5, "0")}
                            </span>
                          </div>
                        </td>
                        <td>
                          <div className="user-info">
                            <div className="avatar">
                              {user?.USER_NAME?.charAt(0)?.toUpperCase()}
                            </div>
                            <div className="user-data">
                              <div className="user-name">{user?.USER_NAME || 'N/A'}</div>
                              {/* <div className="user-email">{user.email}</div> */}
                            </div>
                          </div>
                        </td>
                        <td>
                          <div
                            className="   service-number"
                            style={{ fontWeight: "bold" }}
                          >
                            {user?.SERVICE_NO || 'N/A'}
                          </div>
                        </td>
                        <td>
                          <div className="contact-info">{user?.MO_NO || 'N/A'}</div>
                        </td>
                        <td>
                          <div className="date-display">{user?.DOB || 'N/A'}</div>
                        </td>
                        <td>
                          <div
                            className={`status-badge ${
                              user.IS_STATUS === "1"
                                ? "approved"
                                : user.IS_STATUS === "2"
                                ? "rejected"
                                : "pending"
                            }`}
                            style={{
                              backgroundColor:
                                user.IS_STATUS === "1"
                                  ? "#28a745"
                                  : user.IS_STATUS === "2"
                                  ? "#dc3545"
                                  : "#ffc107",
                              color:
                                user.IS_STATUS === "1"
                                  ? "white"
                                  : user.IS_STATUS === "2"
                                  ? "white"
                                  : "#000",
                              padding: "4px 8px",
                              borderRadius: "4px",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            <i
                              className={`bi ${
                                user.IS_STATUS === "1"
                                  ? "bi-check-circle-fill"
                                  : user.IS_STATUS === "2"
                                  ? "bi-x-circle-fill"
                                  : "bi-hourglass-split"
                              }`}
                            ></i>
                            <span>
                              {user.IS_STATUS === "1"
                                ? "Approved"
                                : user.IS_STATUS === "2"
                                ? "Rejected"
                                : "Pending"}
                            </span>
                          </div>
                        </td>
                        <td>
                          <button
                            onClick={() => handleViewDetails(user)}
                            className="modern-button"
                          >
                            {Icons.ViewDetails}
                            <span>Details</span>
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Modern Pagination */}
            {filteredUsers.length > 0 && (
              <div className="modern-pagination">
                <div className="pagination-info">
                  Showing {indexOfFirstUser + 1} to{" "}
                  {Math.min(indexOfLastUser, filteredUsers.length)} of{" "}
                  {filteredUsers.length} entries
                </div>

                <div className="pagination-controls">
                  <button
                    className="page-button first"
                    onClick={() => paginate(1)}
                    disabled={currentPage === 1}
                  >
                    {Icons.FirstPage}
                  </button>

                  <button
                    className="page-button prev"
                    onClick={prevPage}
                    disabled={currentPage === 1}
                  >
                    {Icons.PrevPage}
                  </button>

                  <div className="page-numbers">
                    {getVisiblePageNumbers().map((page, index) =>
                      page === "..." ? (
                        <span key={`dots-${index}`} className="page-dots">
                          ...
                        </span>
                      ) : (
                        <button
                          key={page}
                          className={`page-button number ${
                            currentPage === page ? "active" : ""
                          }`}
                          onClick={() => paginate(page)}
                        >
                          {page}
                        </button>
                      )
                    )}
                  </div>

                  <button
                    className="page-button next"
                    onClick={nextPage}
                    disabled={currentPage === totalPages}
                  >
                    {Icons.NextPage}
                  </button>

                  <button
                    className="page-button last"
                    onClick={() => paginate(totalPages)}
                    disabled={currentPage === totalPages}
                  >
                    {Icons.LastPage}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </ContentCard>
    </div>
  );
};

export default AresaUserList;
